import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { 
  Crown, Star, Award, User, ChevronRight, Users, TrendingUp, ArrowUpDown, X, Wallet
} from 'lucide-react';
import { ClickToCall } from '../ClickToCall';

import { API_URL } from '../../utils/apiUrl';

const formatCurrency = (value) => {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${value?.toLocaleString('en-IN') || 0}`;
};

const TIER_ICONS = {
  crown: Crown,
  star: Star,
  award: Award,
  user: User
};

// =====================================================
// Holdings Detail Dialog - opened when clicking client AUM
// Fetches /clients/{code}/portfolio and shows ISIN / Quantity / Close Price / Market Value / Buy Avg / Notional P&L
// =====================================================
const HoldingsDetailDialog = ({ clientCode, clientName, token, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientCode) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/clients/${clientCode}/portfolio`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (e) {
        console.error('Failed to fetch portfolio', e);
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [clientCode, token]);

  return (
    <Dialog open={!!clientCode} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0F172A] border-white/10 max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="holdings-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#C5A059]" />
            Holdings Detail
            <span className="ml-2 text-sm text-slate-400">
              {clientCode}{clientName ? ` - ${clientName}` : ''}
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
          </div>
        ) : data ? (
          <div className="flex-1 overflow-auto space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-800/50 border border-white/10 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Total AUM</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(data.total_aum)}</p>
              </div>
              <div className="bg-slate-800/50 border border-white/10 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Notional P&amp;L</p>
                <p className={`text-2xl font-bold ${data.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {data.total_pnl >= 0 ? '+' : ''}{formatCurrency(data.total_pnl)}
                </p>
              </div>
              <div className="bg-slate-800/50 border border-white/10 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Holdings Count</p>
                <p className="text-2xl font-bold text-white">{data.holdings_count}</p>
              </div>
            </div>

            <div className="overflow-x-auto max-h-[400px] overflow-y-auto border border-white/10 rounded-lg">
              <Table>
                <TableHeader className="sticky top-0 bg-[#0F172A]">
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-400">ISIN / Security</TableHead>
                    <TableHead className="text-slate-400 text-right">Quantity</TableHead>
                    <TableHead className="text-slate-400 text-right">Close Price</TableHead>
                    <TableHead className="text-slate-400 text-right">Market Value</TableHead>
                    <TableHead className="text-slate-400 text-right">Buy Avg</TableHead>
                    <TableHead className="text-slate-400 text-right">Notional P&amp;L</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!data.holdings || data.holdings.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                        No holdings found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.holdings.map((h, idx) => (
                      <TableRow key={h.isin || idx} className="border-white/5 hover:bg-white/5">
                        <TableCell>
                          <div>
                            <p className="text-white font-medium">{h.security_name || h.scrip_name || '-'}</p>
                            <p className="text-slate-500 text-xs font-mono">{h.isin || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-white">
                          {(h.quantity || h.qty || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right text-slate-300">
                          ₹{(h.close_price || h.ltp || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right text-white font-medium">
                          {formatCurrency(h.holding_value || h.market_value || 0)}
                        </TableCell>
                        <TableCell className="text-right text-slate-300">
                          ₹{(h.buy_avg || h.avg_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${(h.notional_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {(h.notional_pnl || 0) >= 0 ? '+' : ''}{formatCurrency(h.notional_pnl || 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400">No portfolio data available</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TIER_ICONS_UNUSED = {
  crown: Crown,
  star: Star,
  award: Award,
  user: User
};

const TIER_COLORS = {
  platinum: { bg: 'bg-slate-300/10', border: 'border-slate-300/30', text: 'text-slate-200' },
  gold: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' },
  silver: { bg: 'bg-slate-400/10', border: 'border-slate-400/30', text: 'text-slate-300' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
  default: { bg: 'bg-[#C5A059]/10', border: 'border-[#C5A059]/30', text: 'text-[#C5A059]' }
};

const TierIcon = ({ code, className }) => {
  const icons = {
    platinum: Crown,
    gold: Star,
    silver: Award,
    blue: User
  };
  const IconComponent = icons[code] || Star;
  return <IconComponent className={className} />;
};

// =====================================================
// ADMIN/PRINCIPAL TIER OVERVIEW WIDGET
// =====================================================
export const TierOverviewWidget = ({ token, onDrilldown, compact = false }) => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [tierClients, setTierClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'aum', direction: 'desc' });
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchOverview();
  }, [token]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/tiers/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(res.data);
    } catch (err) {
      console.error('Failed to fetch tier overview:', err);
      setError('Failed to load tier overview');
    } finally {
      setLoading(false);
    }
  };

  const fetchTierClients = async (tierCode) => {
    try {
      setLoadingClients(true);
      const res = await axios.get(`${API_URL}/tiers/clients/${tierCode}?page_size=10000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTierClients(res.data.clients || []);
    } catch (err) {
      console.error('Failed to fetch tier clients:', err);
      setTierClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleTierClick = (tier) => {
    setSelectedTier(tier);
    fetchTierClients(tier.tier_code);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Sort clients locally
  const sortedClients = [...tierClients].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    if (['aum', 'total_brokerage', 'ledger_balance'].includes(sortConfig.key)) {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    } else {
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();
    }
    
    if (sortConfig.direction === 'desc') {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
    return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
  });

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Active</span>;
    }
    return <span className="px-2 py-0.5 rounded-full text-xs bg-slate-500/20 text-slate-400 border border-slate-500/30">{status || 'N/A'}</span>;
  };

  if (loading) {
    return (
      <Card className="card-vault" data-testid="tier-overview-widget">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !overview) {
    return (
      <Card className="card-vault" data-testid="tier-overview-widget">
        <CardContent className="p-6 text-center text-slate-500">
          <p>{error || 'No tier data available'}</p>
        </CardContent>
      </Card>
    );
  }

  const selectedTierColors = selectedTier ? (TIER_COLORS[selectedTier.tier_code] || TIER_COLORS.default) : TIER_COLORS.default;

  return (
    <>
      <Card className="card-vault" data-testid="tier-overview-widget">
        <CardHeader className={`border-b border-white/5 ${compact ? 'py-3' : ''}`}>
          <CardTitle className={`font-heading text-white flex items-center gap-2 ${compact ? 'text-base' : ''}`}>
            <Crown className="w-5 h-5 text-[#C5A059]" />
            Client Tier Overview
          </CardTitle>
        </CardHeader>
        <CardContent className={compact ? 'p-3' : 'p-4'}>
          {/* Summary Stats - Inline for compact mode */}
          <div className={`grid ${compact ? 'grid-cols-4' : 'grid-cols-2'} gap-2 ${compact ? 'mb-3' : 'mb-4'}`}>
            <div className={`${compact ? 'p-2' : 'p-3'} rounded-lg bg-white/5 border border-white/10`}>
              <div className={`flex items-center gap-1 text-slate-400 ${compact ? 'text-[10px]' : 'text-xs'} mb-1`}>
                <Users className="w-3 h-3" /> Clients
              </div>
              <div className={`${compact ? 'text-base' : 'text-xl'} font-bold text-white`}>{overview.total_clients}</div>
            </div>
            <div className={`${compact ? 'p-2' : 'p-3'} rounded-lg bg-white/5 border border-white/10`}>
              <div className={`flex items-center gap-1 text-slate-400 ${compact ? 'text-[10px]' : 'text-xs'} mb-1`}>
                <TrendingUp className="w-3 h-3" /> AUM
              </div>
              <div className={`${compact ? 'text-base' : 'text-xl'} font-bold text-[#C5A059]`}>{formatCurrency(overview.total_aum)}</div>
            </div>
            {compact && overview.tier_distribution?.slice(0, 2).map((tier) => {
              const colors = TIER_COLORS[tier.tier_code] || TIER_COLORS.default;
              return (
                <div key={tier.tier_code} className={`p-2 rounded-lg ${colors.bg} border ${colors.border}`}>
                  <div className="flex items-center gap-1 text-[10px] text-slate-400 mb-1">
                    <TierIcon code={tier.tier_code} className={`w-3 h-3 ${colors.text}`} />
                    {tier.tier_name}
                  </div>
                  <div className="text-base font-bold text-white">{tier.client_count}</div>
                </div>
              );
            })}
          </div>

          {/* Tier Distribution - Compact rows */}
          <div className={`space-y-${compact ? '1' : '2'}`}>
            {overview.tier_distribution?.map((tier) => {
              const colors = TIER_COLORS[tier.tier_code] || TIER_COLORS.default;
              return (
                <div 
                  key={tier.tier_code}
                  onClick={() => handleTierClick(tier)}
                  className={`${compact ? 'p-2' : 'p-3'} rounded-lg ${colors.bg} border ${colors.border} cursor-pointer hover:opacity-80 transition-opacity`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TierIcon code={tier.tier_code} className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} ${colors.text}`} />
                      <div>
                        <div className={`font-medium text-white ${compact ? 'text-sm' : ''}`}>{tier.tier_name}</div>
                        <div className={`${compact ? 'text-[10px]' : 'text-xs'} text-slate-400`}>
                          {tier.client_count} clients ({tier.percentage}%)
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className={`font-medium ${colors.text} ${compact ? 'text-sm' : ''}`}>{formatCurrency(tier.total_aum)}</div>
                        {!compact && <div className="text-xs text-slate-500">AUM</div>}
                      </div>
                      <ChevronRight className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} />
                    </div>
                  </div>
                  {/* Progress Bar - thinner for compact */}
                  <div className={`mt-${compact ? '1' : '2'} h-${compact ? '0.5' : '1'} bg-white/10 rounded-full overflow-hidden`}>
                    <div 
                      className={`h-full ${colors.text.replace('text-', 'bg-')}`}
                      style={{ width: `${tier.percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Full Tier Client List Dialog */}
      <Dialog open={selectedTier !== null} onOpenChange={() => setSelectedTier(null)}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col bg-slate-900 border-white/10">
          <DialogHeader className="border-b border-white/5 pb-4">
            <DialogTitle className="text-white flex items-center gap-3">
              {selectedTier && (
                <>
                  <TierIcon code={selectedTier.tier_code} className={`w-6 h-6 ${selectedTierColors.text}`} />
                  <span className={selectedTierColors.text}>{selectedTier.tier_name}</span>
                  <span className="text-slate-400">• {tierClients.length} Clients</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {loadingClients ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-white/5 hover:bg-transparent">
                    <TableHead 
                      className="text-slate-400 cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('client_code')}
                    >
                      <div className="flex items-center gap-1">
                        Client Code
                        {sortConfig.key === 'client_code' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-slate-400 cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        Client Name
                        {sortConfig.key === 'name' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="text-slate-400">Contact</TableHead>
                    <TableHead 
                      className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('aum')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        AUM
                        {sortConfig.key === 'aum' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('total_brokerage')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Brokerage
                        {sortConfig.key === 'total_brokerage' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('ledger_balance')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Ledger Balance
                        {sortConfig.key === 'ledger_balance' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedClients.map((client, idx) => (
                    <TableRow key={idx} className="border-b border-white/5 hover:bg-white/5">
                      <TableCell>
                        <code className="text-[#C5A059] font-mono text-sm bg-[#C5A059]/10 px-2 py-1 rounded">
                          {client.client_code}
                        </code>
                      </TableCell>
                      <TableCell className="text-white font-medium">{client.name}</TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {client.phone ? (
                          <ClickToCall
                            phoneNumber={client.phone}
                            clientCode={client.client_code}
                            clientName={client.name}
                            token={token}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right text-white">
                        <button
                          type="button"
                          onClick={() => setSelectedClient(client)}
                          className="text-[#C5A059] hover:text-[#D4AF37] hover:underline font-medium"
                          data-testid={`tier-client-aum-${client.client_code}`}
                        >
                          {formatCurrency(client.aum || 0)}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={client.total_brokerage > 0 ? 'text-emerald-400' : 'text-slate-500'}>
                          {client.total_brokerage > 0 ? formatCurrency(client.total_brokerage) : '-'}
                        </span>
                      </TableCell>
                      <TableCell className={`text-right ${
                        client.ledger_balance > 0 ? 'text-emerald-400' : 
                        client.ledger_balance < 0 ? 'text-red-400' : 'text-slate-500'
                      }`}>
                        {client.ledger_balance ? formatCurrency(client.ledger_balance) : '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(client.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <HoldingsDetailDialog
        clientCode={selectedClient?.client_code}
        clientName={selectedClient?.name}
        token={token}
        onClose={() => setSelectedClient(null)}
      />
    </>
  );
};

// =====================================================
// DEALER/RM TIER TABS WIDGET
// =====================================================
export const DealerTierTabsWidget = ({ token }) => {
  const [activeTier, setActiveTier] = useState('platinum');
  const [tiers, setTiers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClients, setLoadingClients] = useState(false);
  const [showFullList, setShowFullList] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'aum', direction: 'desc' });
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchTiers();
  }, [token]);

  useEffect(() => {
    if (activeTier) {
      fetchClientsForTier(activeTier);
    }
  }, [activeTier, token]);

  const fetchTiers = async () => {
    try {
      const res = await axios.get(`${API_URL}/tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiers(res.data.tiers || []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch tiers:', err);
      setLoading(false);
    }
  };

  const fetchClientsForTier = async (tierCode) => {
    try {
      setLoadingClients(true);
      // Fetch ALL clients (no limit)
      const res = await axios.get(`${API_URL}/tiers/clients/${tierCode}?page_size=10000&sort_by=${sortConfig.key}&sort_order=${sortConfig.direction}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(res.data.clients || []);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Sort clients locally
  const sortedClients = [...clients].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    // Handle numeric fields
    if (['aum', 'total_brokerage', 'ledger_balance'].includes(sortConfig.key)) {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    } else {
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();
    }
    
    if (sortConfig.direction === 'desc') {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
    return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
  });

  // Get status badge
  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Active</span>;
    }
    return <span className="px-2 py-0.5 rounded-full text-xs bg-slate-500/20 text-slate-400 border border-slate-500/30">{status || 'N/A'}</span>;
  };

  if (loading) {
    return (
      <Card className="card-vault" data-testid="tier-tabs-widget">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  const activeTierData = tiers.find(t => t.code === activeTier);
  const colors = TIER_COLORS[activeTier] || TIER_COLORS.default;

  return (
    <>
      <Card className="card-vault" data-testid="tier-tabs-widget">
        <CardHeader className="border-b border-white/5">
          <CardTitle className="font-heading text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#C5A059]" />
            My Clients by Tier
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Tier Tabs */}
          <div className="flex border-b border-white/5">
            {tiers.filter(t => t.is_active).map((tier) => {
              const tierColors = TIER_COLORS[tier.code] || TIER_COLORS.default;
              const isActive = activeTier === tier.code;
              const tierClientCount = tier.client_count || 0;
              return (
                <button
                  key={tier.code}
                  onClick={() => setActiveTier(tier.code)}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    isActive 
                      ? `${tierColors.text} border-b-2 ${tierColors.border.replace('border-', 'border-b-')}`
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <TierIcon code={tier.code} className="w-4 h-4" />
                    {tier.name.replace(' Client', '')}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Preview Client List (first 5) */}
          <div className="p-4">
            {loadingClients ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full" />
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No clients in this tier</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {sortedClients.slice(0, 5).map((client) => (
                    <div 
                      key={client.id}
                      className="p-3 rounded-lg bg-white/5 border border-white/10 hover:border-[#C5A059]/30 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-white">{client.name}</div>
                          <div className="text-xs text-slate-400 font-mono">{client.client_code}</div>
                        </div>
                        <div className="text-right">
                          <button
                            type="button"
                            onClick={() => setSelectedClient(client)}
                            className="text-[#C5A059] hover:text-[#D4AF37] hover:underline font-medium"
                            data-testid={`tier-tab-client-aum-${client.client_code}`}
                          >
                            {formatCurrency(client.aum || 0)}
                          </button>
                          <div className="text-xs text-slate-500">AUM</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {clients.length > 5 && (
                  <Button
                    variant="ghost"
                    className="w-full mt-4 text-[#C5A059] hover:text-[#D4AF37] hover:bg-[#C5A059]/10"
                    onClick={() => setShowFullList(true)}
                  >
                    View All {clients.length} {activeTierData?.name || activeTier} Clients
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Full Client List Dialog */}
      <Dialog open={showFullList} onOpenChange={setShowFullList}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col bg-slate-900 border-white/10">
          <DialogHeader className="border-b border-white/5 pb-4">
            <DialogTitle className="text-white flex items-center gap-3">
              <TierIcon code={activeTier} className={`w-6 h-6 ${colors.text}`} />
              <span className={colors.text}>{activeTierData?.name || activeTier}</span>
              <span className="text-slate-400">• {clients.length} Clients</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-white/5 hover:bg-transparent">
                  <TableHead 
                    className="text-slate-400 cursor-pointer hover:text-[#C5A059]"
                    onClick={() => handleSort('client_code')}
                  >
                    <div className="flex items-center gap-1">
                      Client Code
                      {sortConfig.key === 'client_code' && (
                        <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-slate-400 cursor-pointer hover:text-[#C5A059]"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Client Name
                      {sortConfig.key === 'name' && (
                        <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="text-slate-400">Contact</TableHead>
                  <TableHead 
                    className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059]"
                    onClick={() => handleSort('aum')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      AUM
                      {sortConfig.key === 'aum' && (
                        <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059]"
                    onClick={() => handleSort('total_brokerage')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Brokerage
                      {sortConfig.key === 'total_brokerage' && (
                        <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059]"
                    onClick={() => handleSort('ledger_balance')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Ledger Balance
                      {sortConfig.key === 'ledger_balance' && (
                        <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedClients.map((client, idx) => (
                  <TableRow key={idx} className="border-b border-white/5 hover:bg-white/5">
                    <TableCell>
                      <code className="text-[#C5A059] font-mono text-sm bg-[#C5A059]/10 px-2 py-1 rounded">
                        {client.client_code}
                      </code>
                    </TableCell>
                    <TableCell className="text-white font-medium">{client.name}</TableCell>
                    <TableCell className="text-slate-300 text-sm">
                      {client.phone ? (
                        <ClickToCall
                          phoneNumber={client.phone}
                          clientCode={client.client_code}
                          clientName={client.name}
                          token={token}
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right text-white">
                      <button
                        type="button"
                        onClick={() => setSelectedClient(client)}
                        className="text-[#C5A059] hover:text-[#D4AF37] hover:underline font-medium"
                        data-testid={`tier-full-client-aum-${client.client_code}`}
                      >
                        {formatCurrency(client.aum || 0)}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={client.total_brokerage > 0 ? 'text-emerald-400' : 'text-slate-500'}>
                        {client.total_brokerage > 0 ? formatCurrency(client.total_brokerage) : '-'}
                      </span>
                    </TableCell>
                    <TableCell className={`text-right ${
                      client.ledger_balance > 0 ? 'text-emerald-400' : 
                      client.ledger_balance < 0 ? 'text-red-400' : 'text-slate-500'
                    }`}>
                      {client.ledger_balance ? formatCurrency(client.ledger_balance) : '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(client.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
      <HoldingsDetailDialog
        clientCode={selectedClient?.client_code}
        clientName={selectedClient?.name}
        token={token}
        onClose={() => setSelectedClient(null)}
      />
    </>
  );
};

// =====================================================
// CLIENT TIER BADGE (for Client Portal)
// =====================================================
export const ClientTierBadge = ({ tierCode, tierName }) => {
  const colors = TIER_COLORS[tierCode] || TIER_COLORS.default;
  
  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${colors.bg} border ${colors.border}`}>
      <TierIcon code={tierCode} className={`w-5 h-5 ${colors.text}`} />
      <span className={`font-medium ${colors.text}`}>{tierName}</span>
    </div>
  );
};

// =====================================================
// CLIENT TIER DISPLAY CARD (for Client Dashboard)
// =====================================================
export const ClientTierCard = ({ tierCode, tierName }) => {
  const colors = TIER_COLORS[tierCode] || TIER_COLORS.default;
  
  return (
    <Card className="card-vault">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-full ${colors.bg} border ${colors.border} flex items-center justify-center`}>
            <TierIcon code={tierCode} className={`w-8 h-8 ${colors.text}`} />
          </div>
          <div>
            <p className="text-sm text-slate-400">Your Client Status</p>
            <p className={`text-2xl font-bold ${colors.text}`}>{tierName}</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          This classification is for informational purposes only and is based on your portfolio value.
        </p>
      </CardContent>
    </Card>
  );
};

export default { TierOverviewWidget, DealerTierTabsWidget, ClientTierBadge, ClientTierCard };
